// Dados públicos junto da consulta (2026-10-10) — o backend cruza o CPF com
// o Portal da Transparência (CGU) e busca na Receita (dados abertos) as
// empresas em que a pessoa é sócia; vem em `result.publicData`. Usa as
// classes cx-* definidas em routes/consultas.tsx.
import type { ReactNode } from "react";
import {
  Award,
  Banknote,
  Briefcase,
  Building2,
  Landmark,
  Plane,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Status = "ok" | "not_configured" | "error";
interface Section<T> {
  status: Status;
  message?: string;
  items: T[];
}
export interface PublicData {
  fetchedAt: string;
  appearsIn: string[];
  transparency: { status: Status; message?: string };
  sanctions: Section<{
    list: string;
    type?: string;
    organ?: string;
    start?: string;
    end?: string;
    link?: string;
    detail?: string;
  }>;
  peps: Section<{ role?: string; organ?: string; start?: string; end?: string }>;
  servants: Section<{
    situation?: string;
    kind?: string;
    organ?: string;
    role?: string;
    since?: string;
    uf?: string;
  }>;
  benefits: Section<{
    program: string;
    month?: string;
    value?: number;
    city?: string;
    detail?: string;
  }>;
  travels: Section<{
    year?: number;
    reason?: string;
    organ?: string;
    start?: string;
    end?: string;
    value?: number;
  }>;
  companies: Section<{
    cnpj: string;
    name?: string;
    tradeName?: string;
    status?: string;
    openedAt?: string;
    activity?: string;
    city?: string;
    size?: string;
    capital?: number;
    partners: { name: string; role?: string; since?: string }[];
  }>;
}

export function publicDataOf(result: unknown): PublicData | null {
  const pd =
    result && typeof result === "object" ? (result as { publicData?: unknown }).publicData : null;
  return pd && typeof pd === "object" && "transparency" in pd ? (pd as PublicData) : null;
}

export function publicDataCount(pd: PublicData | null): number | undefined {
  if (!pd) return undefined;
  return (
    pd.sanctions.items.length +
    pd.peps.items.length +
    pd.servants.items.length +
    pd.benefits.items.length +
    pd.travels.items.length +
    pd.companies.items.length
  );
}

export const brl = (v?: number) =>
  v == null ? undefined : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
export const cnpjMask = (c: string) =>
  c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
const period = (a?: string, b?: string) => [a, b].filter(Boolean).join(" → ") || undefined;

// Cada programa do Portal manda o mês num formato ("07/2021", "2023-02-01").
// Chave AAAAMM pra ordenar + texto MM/AAAA pra mostrar.
export function monthKey(m?: string): string {
  if (!m) return "";
  let x = /^(\d{4})-(\d{2})/.exec(m);
  if (x) return x[1] + x[2];
  x = /^(\d{2})\/(\d{4})$/.exec(m);
  if (x) return x[2] + x[1];
  x = /^(\d{4})(\d{2})$/.exec(m);
  return x ? x[1] + x[2] : m;
}
export const monthText = (m?: string) => {
  const k = monthKey(m);
  return /^\d{6}$/.test(k) ? `${k.slice(4)}/${k.slice(0, 4)}` : m;
};
// "Não há" = campo vazio na fonte (situação do Auxílio Emergencial).
export const cleanDetail = (d?: string) =>
  d
    ?.split(" · ")
    .filter((p) => !/^n[ãa]o h[áa]$/i.test(p.trim()))
    .join(" · ") || undefined;

type Benefit = PublicData["benefits"]["items"][number];
export function benefitSummary(items: Benefit[]) {
  const by = new Map<string, { count: number; total: number; first: string; last: string }>();
  for (const b of items) {
    const k = monthKey(b.month);
    const g = by.get(b.program) ?? { count: 0, total: 0, first: k, last: k };
    g.count++;
    g.total += b.value ?? 0;
    if (k && (!g.first || k < g.first)) g.first = k;
    if (k > g.last) g.last = k;
    by.set(b.program, g);
  }
  return [...by.entries()].sort((a, b) => b[1].last.localeCompare(a[1].last));
}

function Chip({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: "slate" | "green" | "amber" | "rose" | "cyan";
}) {
  const tones = {
    slate: "border-slate-500/40 bg-slate-500/10 text-slate-300",
    green: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
    cyan: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300",
    amber: "border-amber-400/40 bg-amber-400/10 text-amber-300",
    rose: "border-rose-400/40 bg-rose-400/10 text-rose-300",
  };
  return (
    <span
      className={cn(
        "rounded border px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.08em]",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-[56px] items-center justify-center rounded-lg border border-dashed border-slate-700/60 px-3 text-center text-xs text-slate-500">
      {children}
    </div>
  );
}

function Block<T>({
  title,
  icon: Icon,
  section,
  empty = "Nada consta.",
  className,
  header,
  render,
}: {
  title: string;
  icon: LucideIcon;
  section: Section<T>;
  empty?: string;
  className?: string;
  header?: ReactNode;
  render: (item: T, i: number) => ReactNode;
}) {
  return (
    <div className={cn("cx-card flex flex-col p-4", className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="cx-title">
          <Icon className="h-3.5 w-3.5" /> {title}
        </div>
        {section.status === "ok" && (
          <span className="cx-mono text-[11px] text-slate-500">
            {section.items.length.toString().padStart(2, "0")}
          </span>
        )}
      </div>
      {section.status === "not_configured" ? (
        <Note>Fonte pública ainda em ativação.</Note>
      ) : section.status === "error" && !section.items.length ? (
        <Note>Fonte indisponível no momento{section.message ? ` (${section.message})` : ""}.</Note>
      ) : !section.items.length ? (
        <Note>
          <span className="text-emerald-300/80">{empty}</span>
        </Note>
      ) : (
        <>
          {header}
          <ul className="cx-scroll max-h-[420px] space-y-2 overflow-y-auto pr-1">
            {section.items.map((it, i) => (
              <li key={i} className="cx-row p-3">
                {render(it, i)}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Line({
  main,
  sub,
  tag,
}: {
  main?: string;
  sub?: (string | undefined)[];
  tag?: ReactNode;
}) {
  const subText = (sub ?? []).filter(Boolean).join(" · ");
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[13px] font-medium leading-snug text-slate-100">{main ?? "—"}</div>
        {subText && <div className="mt-0.5 text-[11px] text-slate-400">{subText}</div>}
      </div>
      {tag}
    </div>
  );
}

/** Resumo para a "Visão geral". */
export function PublicDataSummary({
  data,
  className,
  onOpen,
}: {
  data: PublicData | null;
  className?: string;
  onOpen: () => void;
}) {
  return (
    <div className={cn("cx-card flex flex-col p-4", className)}>
      <div className="mb-3 cx-title">
        <Landmark className="h-3.5 w-3.5" /> Dados públicos
      </div>
      {!data ? (
        <Note>Consulta feita antes da busca em dados públicos.</Note>
      ) : data.transparency.status === "not_configured" && !data.companies.items.length ? (
        <Note>Fonte pública ainda em ativação.</Note>
      ) : data.appearsIn.length || data.companies.items.length ? (
        <div className="flex flex-wrap gap-1.5">
          {data.appearsIn.map((a) => (
            <Chip key={a} tone={/CEIS|CNEP|CEAF/.test(a) ? "rose" : "cyan"}>
              {a}
            </Chip>
          ))}
          {data.companies.items.length > 0 && (
            <Chip tone="green">{data.companies.items.length} empresa(s) na Receita</Chip>
          )}
        </div>
      ) : (
        <Note>
          <span className="text-emerald-300/80">Nada consta no Portal da Transparência.</span>
        </Note>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="mt-3 self-start text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-300 hover:text-emerald-200"
      >
        Ver detalhes →
      </button>
    </div>
  );
}

/** Aba completa. */
export function PublicDataPanel({ data }: { data: PublicData | null }) {
  if (!data)
    return (
      <div className="cx-card px-6 py-10 text-center text-sm text-slate-400">
        Esta consulta foi feita antes da busca em dados públicos.
      </div>
    );
  return (
    <div className="space-y-4">
      <div className="cx-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="cx-title">
            <Landmark className="h-3.5 w-3.5" /> Onde o CPF aparece no Portal da Transparência
          </div>
          <span className="text-[10px] text-slate-500">
            Fontes: Portal da Transparência (CGU) e dados abertos da Receita Federal ·{" "}
            {new Date(data.fetchedAt).toLocaleString("pt-BR")}
          </span>
        </div>
        <div className="mt-3">
          {data.transparency.status === "not_configured" ? (
            <Note>Fonte pública ainda em ativação.</Note>
          ) : data.transparency.status === "error" ? (
            <Note>
              Fonte indisponível no momento
              {data.transparency.message ? ` (${data.transparency.message})` : ""}.
            </Note>
          ) : data.appearsIn.length ? (
            <div className="flex flex-wrap gap-1.5">
              {data.appearsIn.map((a) => (
                <Chip key={a} tone={/CEIS|CNEP|CEAF/.test(a) ? "rose" : "cyan"}>
                  {a}
                </Chip>
              ))}
            </div>
          ) : (
            <Note>
              <span className="text-emerald-300/80">
                Nada consta — o CPF não aparece em nenhum cadastro do Portal da Transparência.
              </span>
            </Note>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Block
          title="Sanções e punições"
          icon={ShieldAlert}
          section={data.sanctions}
          empty="Nada consta em CEIS, CNEP e CEAF."
          render={(s) => (
            <>
              <Line
                main={s.type ?? "Sanção"}
                sub={[s.organ, period(s.start, s.end), s.detail]}
                tag={<Chip tone="rose">{s.list}</Chip>}
              />
              {s.link && (
                <a
                  href={s.link}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-[11px] font-semibold text-emerald-300 hover:text-emerald-200"
                >
                  Publicação oficial →
                </a>
              )}
            </>
          )}
        />
        <Block
          title="Pessoa politicamente exposta"
          icon={Award}
          section={data.peps}
          empty="Não é pessoa politicamente exposta."
          render={(p) => (
            <Line
              main={p.role}
              sub={[p.organ, period(p.start, p.end)]}
              tag={<Chip tone="amber">PEP</Chip>}
            />
          )}
        />
        <Block
          title="Servidor público federal"
          icon={Briefcase}
          section={data.servants}
          empty="Sem vínculo com o Executivo Federal."
          render={(s) => (
            <Line
              main={s.role ?? s.kind}
              sub={[s.organ, s.uf, s.since && `desde ${s.since}`]}
              tag={s.situation ? <Chip>{s.situation}</Chip> : undefined}
            />
          )}
        />
        <Block
          title="Benefícios sociais"
          icon={Banknote}
          section={{
            ...data.benefits,
            items: [...data.benefits.items].sort((a, b) =>
              monthKey(b.month).localeCompare(monthKey(a.month)),
            ),
          }}
          empty="Nenhum benefício social encontrado."
          header={
            data.benefits.items.length > 0 && (
              <div className="mb-3 grid gap-2 sm:grid-cols-2">
                {benefitSummary(data.benefits.items).map(([program, g]) => (
                  <div
                    key={program}
                    className="rounded-lg border border-cyan-400/20 bg-cyan-400/5 px-3 py-2"
                  >
                    <div className="text-[12px] font-semibold text-cyan-200">{program}</div>
                    <div className="text-[11px] text-slate-400">
                      {g.count} parcela(s) ·{" "}
                      <span className="cx-mono text-emerald-300">{brl(g.total)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {period(
                        monthText(g.first),
                        g.last !== g.first ? monthText(g.last) : undefined,
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          }
          render={(b) => (
            <Line
              main={b.program}
              sub={[monthText(b.month), b.city, cleanDetail(b.detail)]}
              tag={
                b.value != null ? (
                  <span className="cx-mono text-xs text-emerald-300">{brl(b.value)}</span>
                ) : undefined
              }
            />
          )}
        />
        <Block
          title="Viagens a serviço do governo"
          icon={Plane}
          section={data.travels}
          empty="Nenhuma viagem paga pelo governo federal."
          render={(t) => (
            <Line
              main={t.reason ?? "Viagem"}
              sub={[t.organ, period(t.start, t.end)]}
              tag={
                t.value != null ? (
                  <span className="cx-mono text-xs text-slate-300">{brl(t.value)}</span>
                ) : undefined
              }
            />
          )}
        />
        <Block
          title="Empresas (Receita Federal)"
          icon={Building2}
          section={data.companies}
          empty="Nenhuma empresa vinculada a este CPF."
          render={(c) => (
            <>
              <Line
                main={c.name ?? cnpjMask(c.cnpj)}
                sub={[
                  cnpjMask(c.cnpj),
                  c.tradeName,
                  c.activity,
                  c.city,
                  c.openedAt && `aberta em ${c.openedAt}`,
                  c.capital != null ? `capital ${brl(c.capital)}` : undefined,
                ]}
                tag={
                  c.status ? (
                    <Chip tone={/ativa/i.test(c.status) ? "green" : "amber"}>{c.status}</Chip>
                  ) : undefined
                }
              />
              {c.partners.length > 0 && (
                <div className="mt-2 border-t border-emerald-400/10 pt-2 text-[11px] text-slate-400">
                  <span className="cx-label">Sócios</span>
                  <ul className="mt-1 space-y-0.5">
                    {c.partners.map((p, i) => (
                      <li key={i}>
                        <span className="text-slate-200">{p.name}</span>
                        {p.role && ` · ${p.role}`}
                        {p.since && ` · desde ${p.since}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
}
